import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STATUS_LABELS, STATUS_LIST } from "@/lib/constants";
import { Play, Pause, RotateCcw, Save, Upload, X, FileText } from "lucide-react";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors";

export default function NuovoMontaggio() {
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [parts, setParts] = useState([]);
  const [operators, setOperators] = useState([]);

  const [form, setForm] = useState({
    commessa: "",
    line_id: "",
    part_id: "",
    operator_id: "",
    date: new Date().toISOString().slice(0, 10),
    due_date: "",
    status: "in_corso",
    notes: "",
  });

  // timer
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [manualMinutes, setManualMinutes] = useState("");
  const timerRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/lines"), api.get("/parts"), api.get("/operators")]).then(
      ([l, p, o]) => {
        setLines(l.data);
        setParts(p.data);
        setOperators(o.data);
      }
    );
  }, []);

  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [running]);

  const timeMinutes = useMemo(() => {
    if (manualMinutes !== "") return Number(manualMinutes) || 0;
    return Math.round((seconds / 60) * 10) / 10;
  }, [seconds, manualMinutes]);

  const hh = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.commessa || !form.line_id || !form.part_id) {
      toast.error("Compila commessa, linea e componente.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, time_minutes: timeMinutes };
      if (!payload.operator_id) delete payload.operator_id;
      const { data } = await api.post("/assemblies", payload);
      // upload attachments
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/assemblies/${data.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Montaggio registrato con successo.");
      navigate("/storico");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl animate-fade-up">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Registra Montaggio</h1>
      <p className="text-slate-500 text-sm mt-1 mb-6">
        Cronometra il montaggio o inserisci il tempo manualmente, allega foto e disegni.
      </p>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left form */}
        <div className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Commessa">
              <input
                data-testid="input-commessa"
                className={inputCls}
                placeholder="COM-2026-XXX"
                value={form.commessa}
                onChange={(e) => set("commessa", e.target.value)}
              />
            </Field>
            <Field label="Data">
              <input
                type="date"
                data-testid="input-date"
                className={inputCls}
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
              />
            </Field>
            <Field label="Scadenza prevista">
              <input
                type="date"
                data-testid="input-due-date"
                className={inputCls}
                value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)}
              />
            </Field>
            <Field label="Linea di montaggio">
              <select
                data-testid="select-line"
                className={inputCls}
                value={form.line_id}
                onChange={(e) => set("line_id", e.target.value)}
              >
                <option value="">Seleziona linea…</option>
                {lines.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Componente / Parte">
              <select
                data-testid="select-part"
                className={inputCls}
                value={form.part_id}
                onChange={(e) => set("part_id", e.target.value)}
              >
                <option value="">Seleziona parte…</option>
                {parts.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                ))}
              </select>
            </Field>
            <Field label="Operatore">
              <select
                data-testid="select-operator"
                className={inputCls}
                value={form.operator_id}
                onChange={(e) => set("operator_id", e.target.value)}
              >
                <option value="">Io stesso</option>
                {operators.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Stato">
              <select
                data-testid="select-status"
                className={inputCls}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {STATUS_LIST.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Note tecniche">
            <textarea
              data-testid="input-notes"
              rows={3}
              className="w-full rounded-lg bg-slate-900 border border-slate-700 px-4 py-3 text-sm outline-none focus:border-amber-500 transition-colors resize-none"
              placeholder="Osservazioni, criticità, ricambi utilizzati…"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>

          {/* Attachments */}
          <Field label="Allegati (foto / disegni PDF)">
            <label
              className="flex items-center gap-3 h-11 px-4 rounded-lg border border-dashed border-slate-700 hover:border-amber-500 cursor-pointer text-sm text-slate-400 transition-colors"
              data-testid="file-upload-input"
            >
              <Upload className="h-4 w-4" />
              Aggiungi file…
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => setPendingFiles((f) => [...f, ...Array.from(e.target.files)])}
              />
            </label>
            {pendingFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm bg-slate-800/60 rounded-lg px-3 py-2">
                    <FileText className="h-4 w-4 text-amber-400" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setPendingFiles((p) => p.filter((_, idx) => idx !== i))}
                      className="text-slate-500 hover:text-rose-400"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>

        {/* Timer panel */}
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6 h-fit">
          <h2 className="font-heading text-lg font-bold mb-1">Cronometro</h2>
          <p className="text-xs text-slate-500 mb-5">Tempo di montaggio effettivo.</p>
          <div className="text-center py-4 rounded-lg bg-slate-950 border border-slate-800 mb-4">
            <span className="font-mono-data text-4xl font-bold tracking-widest text-amber-400" data-testid="timer-display">
              {hh}:{mm}:{ss}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-5">
            {!running ? (
              <button
                type="button"
                onClick={() => { setRunning(true); setManualMinutes(""); }}
                data-testid="timer-start-btn"
                className="col-span-1 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center gap-1.5 text-sm hover:bg-emerald-500/25 transition-colors"
              >
                <Play className="h-4 w-4" /> Avvia
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setRunning(false)}
                data-testid="timer-stop-btn"
                className="col-span-1 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center gap-1.5 text-sm hover:bg-amber-500/25 transition-colors"
              >
                <Pause className="h-4 w-4" /> Pausa
              </button>
            )}
            <button
              type="button"
              onClick={() => { setSeconds(0); setRunning(false); }}
              className="col-span-2 h-10 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 flex items-center justify-center gap-1.5 text-sm hover:bg-slate-700 transition-colors"
            >
              <RotateCcw className="h-4 w-4" /> Azzera
            </button>
          </div>
          <Field label="Oppure minuti manuali">
            <input
              type="number"
              min="0"
              data-testid="input-manual-minutes"
              className={inputCls}
              placeholder="es. 45"
              value={manualMinutes}
              onChange={(e) => setManualMinutes(e.target.value)}
            />
          </Field>
          <div className="mt-4 text-sm text-slate-400">
            Tempo registrato: <span className="font-mono-data text-amber-400">{timeMinutes} min</span>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="save-assembly-btn"
            className="mt-6 w-full h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> Salva Montaggio
          </button>
        </div>
      </form>
    </div>
  );
}
